import { converter, formatCss, parse } from 'culori';

import type { TenantTheme } from '~/constants/config/tenant';

const toOklch = converter('oklch');

/**
 * Convert a hex colour string to an oklch() CSS value.
 * Returns the original string if parsing fails.
 */
function hexToOklch(hex: string): string {
  const parsed = parse(hex);
  if (!parsed) return hex;

  const oklch = toOklch(parsed);
  return formatCss(oklch);
}

/**
 * Compute a foreground colour (light or dark) that provides WCAG AA contrast
 * against the given background hex colour.
 */
function contrastForeground(hex: string): string {
  const parsed = parse(hex);
  if (!parsed) return 'oklch(1 0 0)';

  const oklch = toOklch(parsed);

  // Use lightness threshold — oklch L > 0.6 → dark foreground, else light
  return oklch.l > 0.6 ? 'oklch(0.205 0 0)' : 'oklch(0.985 0 0)';
}

/**
 * Inject a custom font via a <link> element if a fontUrl is provided.
 * Idempotent — will not add duplicate links.
 */
function injectFont(fontUrl: string): void {
  const linkId = 'tenant-custom-font';
  const existing = document.getElementById(linkId);
  if (existing) {
    if (existing.getAttribute('href') === fontUrl) return;
    existing.remove();
  }

  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = fontUrl;
  document.head.appendChild(link);
}

/**
 * Apply a tenant theme by setting CSS custom properties on the root element.
 * Uses only `element.style.setProperty()` — never innerHTML or raw CSS injection.
 *
 * Also caches the theme in sessionStorage to prevent FOUC on reload.
 */
export function applyTenantTheme(
  theme: TenantTheme,
  root: HTMLElement = document.documentElement,
): void {
  const style = root.style;

  // Primary
  style.setProperty('--primary', hexToOklch(theme.primaryColor));
  style.setProperty('--primary-foreground', contrastForeground(theme.primaryColor));

  // Secondary
  style.setProperty('--secondary', hexToOklch(theme.secondaryColor));
  style.setProperty('--secondary-foreground', contrastForeground(theme.secondaryColor));

  // Sync extended brand/accent tokens with tenant overrides
  style.setProperty('--brand', hexToOklch(theme.primaryColor));
  style.setProperty('--accent-main', hexToOklch(theme.secondaryColor));
  style.setProperty('--ring', hexToOklch(theme.secondaryColor));
  style.setProperty('--sidebar-primary', hexToOklch(theme.primaryColor));
  style.setProperty('--sidebar-ring', hexToOklch(theme.secondaryColor));

  // Background
  style.setProperty('--background', hexToOklch(theme.backgroundColor));
  style.setProperty('--foreground', contrastForeground(theme.backgroundColor));

  // Border radius
  style.setProperty('--radius', theme.borderRadius);

  // Font
  if (theme.fontFamily) {
    style.setProperty('--font-sans', `'${theme.fontFamily}', sans-serif`);
  }

  if (theme.fontUrl) {
    injectFont(theme.fontUrl);
  }

  // Favicon
  if (theme.faviconUrl) {
    const faviconLink =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]') ?? document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.href = theme.faviconUrl;
    if (!faviconLink.parentNode) {
      document.head.appendChild(faviconLink);
    }
  }

  // Cache for FOUC prevention
  try {
    sessionStorage.setItem('tenant-theme', JSON.stringify(theme));
  } catch {
    // sessionStorage may be unavailable — ignore
  }
}
